import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { FormProvider, useForm } from 'react-hook-form';

import { GET_MEMBERSHIP_DETAILS } from 'store/queries';
import { Row, Column } from 'components/shared/Layout';
import Input from 'components/ui/Input';
import Label from 'components/ui/Label';
import Button from 'components/ui/Button';
import { isValidEmail, isValidPhoneNumberWithCountryCode, isValidGroupName, isValidCountryCode, isValidNumber } from 'utils/validators';
import { bookingShape, clientShape, passengerShape, historyShape } from 'constants/propTypes';
import { hasGroupDetailsForm } from 'utils/isGroupBooking';
import EmailLanguage from 'components/ui/EmailLanguage';
import { getPassengerTypeCounts } from 'utils/minors';
import { types as passengerTypes } from 'types/passengers';
import RadioFieldSet from 'components/ui/RadioFieldSet';
import AlertBox from 'components/ui/AlertBox';
import { isEurostarOffer } from 'utils/isEurostarOffer';
import { parsePhoneNumber, formatPhoneNumber } from 'utils/parsePhoneNumber';

import DiallingCodesSelector from '../../components/ui/DiallingCodesSelector';

import GroupBookingSeatDetails from './GroupBookingSeatDetails';
import { PhoneWrapper, PhoneItemWrapper, StyledForm, ErrorMessage } from './GroupDetails.styles';
import { getDefaultGroupFormValues } from './GroupDetails.utils';

const options = [
  { value: 'no', label: <FormattedMessage id="interaction.no" /> },
  { value: 'yes', label: <FormattedMessage id="interaction.yes" /> }
];

const GroupDetails = ({
  bookingData = {},
  selectedSeats,
  selectedPassenger,
  selectedPassengers,
  updatePassengerSeats,
  onSelectNewPassenger,
  onRemovePassengerSelection,
  updatePassenger,
  updateMinors,
  bookingLanguage,
  setBookingLanguage,
  isAssistanceSelected,
  canAddSpecialAssistanceToBooking,
  setIsAssistanceSelected,
  membershipNumber,
  client,
  loading,
  isDisabled,
  disabledContinue,
  history
}) => {
  const {
    reference: bookingReference,
    passengers,
    customer,
    additionalDetails,
    productFamilyGroup,
    outbound = [],
    inbound = []
  } = bookingData;

  const { knownMinors, totalPassengerCount } = useMemo(() => {
    const passengerTypeCounts = getPassengerTypeCounts({ passengers });
    const knownMinors = passengerTypeCounts[passengerTypes.CH] + passengerTypeCounts[passengerTypes.CHILD];
    const totalPassengerCount = passengerTypeCounts.total;

    return {
      passengerTypeCounts,
      knownMinors,
      totalPassengerCount
    };
  }, [passengers]);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: getDefaultGroupFormValues({ customer, additionalDetails, knownMinors })
  });

  const { handleSubmit, register, getValues, trigger, formState } = formMethods;
  const { errors } = formState;

  const handleSubmitGroupForm = (updatePassengerSeats) => async (values) => {
    const { groupName, email, countryCode, phoneNumber, numberOfMinors } = values ?? {};

    const contactNumber = `${countryCode}${phoneNumber.replace(/^0+/, '')}`;

    let customerInfo = null;

    if (membershipNumber) {
      try {
        const result = await client?.query?.({
          query: GET_MEMBERSHIP_DETAILS,
          variables: {
            membershipNumber: membershipNumber
          }
        });
        customerInfo = result?.data?.getCustomerDetails;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Cannot get customer info', error);
      }
    }

    await updatePassengerSeats();

    const trimmedGroupName = groupName.trim();

    await updatePassenger({
      variables: {
        bookingReference,
        passengers: Object.values(passengers).map(({ id }) => ({
          id,
          email,
          firstName: trimmedGroupName,
          lastName: trimmedGroupName,
          phoneNumber: contactNumber
        })),
        customer: customerInfo
          ? {
              firstName: customerInfo?.firstName.trim() || '',
              lastName: customerInfo?.lastName.trim() || '',
              email: customerInfo?.emailAddress || '',
              companyName: trimmedGroupName,
              ...(customerInfo?.phoneNumber && {
                phoneNumber: formatPhoneNumber(parsePhoneNumber(customerInfo.phoneNumber)) || customerInfo.phoneNumber.replace(/^0+/, '')
              }),
              address: {
                ...(customerInfo.address?.street && { street: customerInfo.address?.street }),
                ...(customerInfo.address?.postalCode && { postalCode: customerInfo.address?.postalCode }),
                ...(customerInfo.address?.city && { city: customerInfo.address?.city }),
                ...(customerInfo.address?.countryCode && { countryCode: customerInfo.address?.countryCode })
              }
            }
          : {
              companyName: trimmedGroupName,
              email,
              phoneNumber: contactNumber
            }
      }
    });

    if (updateMinors) {
      await updateMinors(numberOfMinors);
    }
  };

  if (!hasGroupDetailsForm(productFamilyGroup)) {
    return (
      <Redirect to={{ pathname: `/booking/${bookingReference}/pax`, search: history?.location?.search, state: history?.location?.state }} />
    );
  }

  const selectedAssistanceOption = isAssistanceSelected ? options[1].value : options[0].value;

  const shouldBlockAddingAssistance =
    isAssistanceSelected && !(canAddSpecialAssistanceToBooking && isEurostarOffer([...outbound, ...inbound]));

  const handleIsAssistanceSelected = () => setIsAssistanceSelected(!isAssistanceSelected);
  const handleCountryCodeChange = () => trigger('phoneNumber');

  return (
    <FormProvider {...formMethods}>
      <StyledForm autoComplete="off" onSubmit={handleSubmit(handleSubmitGroupForm(updatePassengerSeats))}>
        <Row span={16} columns={16}>
          <Column span={3}>
            <Label htmlFor="groupName">
              <FormattedMessage id="groupDetails.groupName" />
            </Label>
            <Input
              {...register('groupName', {
                required: 'groupDetails.groupNameEmptyError',
                minLength: {
                  value: 2,
                  message: 'groupDetails.groupNameEmptyError'
                },
                validate: (value) => isValidGroupName(value) || 'groupDetails.groupNameValidationError'
              })}
              data-cy="groupName"
              data-testid="groupName"
              id="groupName"
              type="text"
              autoFocus
              disabled={isDisabled}
            />
            {errors?.groupName && (
              <ErrorMessage data-error="groupNameError">
                <FormattedMessage id={errors.groupName.message} />
              </ErrorMessage>
            )}
          </Column>
          <Column span={3}>
            <Label htmlFor="email">
              <FormattedMessage id="groupDetails.contactEmail" />
            </Label>
            <Input
              data-cy="email"
              id="email"
              type="text"
              inputMode="email"
              {...register('email', {
                required: 'groupDetails.emailError',
                validate: (value) => isValidEmail(value) || 'groupDetails.emailError'
              })}
              disabled={isDisabled}
            />
            {errors?.email && (
              <ErrorMessage data-error="emailError">
                <FormattedMessage id={errors.email.message} />
              </ErrorMessage>
            )}
          </Column>
          <Column span={2}>
            <Label htmlFor="bookingLanguage">
              <FormattedMessage id="groupDetails.language" />
            </Label>
            <div>
              <EmailLanguage initialValue={bookingLanguage} onChange={(event) => setBookingLanguage(event.value)} height="29px" />
            </div>
          </Column>
          <Column span={5}>
            <PhoneWrapper>
              <PhoneItemWrapper>
                <Label htmlFor="phoneCountryCode">
                  <FormattedMessage id="paxForm.countryCode" />
                </Label>
                <div>
                  <DiallingCodesSelector
                    required
                    validate={isValidCountryCode}
                    data-cy="countryCode"
                    name="countryCode"
                    id="countryCode"
                    classes="styled-passenger-form-code-selector"
                    disabled={isDisabled}
                    onChange={handleCountryCodeChange}
                  />
                </div>
              </PhoneItemWrapper>
              <PhoneItemWrapper isFullWidth>
                <Label htmlFor="phoneNumber">
                  <FormattedMessage id="paxForm.phoneNumber" />
                </Label>
                <Input
                  data-cy="phoneNumber"
                  type="number"
                  inputMode="tel"
                  id="phoneNumber"
                  {...register('phoneNumber', {
                    required: 'groupDetails.phoneNumberError',
                    validate: (value) =>
                      isValidPhoneNumberWithCountryCode(value, getValues('countryCode')) || 'groupDetails.phoneNumberError'
                  })}
                  disabled={isDisabled}
                />
              </PhoneItemWrapper>
            </PhoneWrapper>
            {errors?.countryCode || errors?.phoneNumber ? (
              <ErrorMessage data-error="phoneNumberError">
                <FormattedMessage id={errors?.countryCode ? 'groupDetails.countryCodeError' : errors.phoneNumber.message} />
              </ErrorMessage>
            ) : null}
          </Column>
          <Column span={3}>
            <Label htmlFor="numberOfMinors">
              <FormattedMessage id="groupDetails.numberOfMinors" />
            </Label>
            <Input
              data-cy="numberOfMinors"
              id="numberOfMinors"
              type="number"
              {...register('numberOfMinors', {
                required: 'groupDetails.numberOfMinorsError',
                validate: (value) => isValidNumber(value) || 'groupDetails.numberOfMinorsError',
                max: {
                  value: totalPassengerCount,
                  message: 'groupDetails.numberOfMinorsError'
                },
                min: {
                  value: knownMinors,
                  message: 'groupDetails.numberOfMinorsError'
                }
              })}
              disabled={isDisabled}
            />
            {errors?.numberOfMinors && (
              <ErrorMessage data-error="numberOfMinorsError">
                <FormattedMessage id={errors.numberOfMinors.message} />
              </ErrorMessage>
            )}
          </Column>
        </Row>
        <Row span={16} columns={16}>
          <Column span={16}>
            <GroupBookingSeatDetails
              {...{
                selectedSeats,
                onSelectNewPassenger,
                selectedPassenger,
                selectedPassengers,
                onRemovePassengerSelection
              }}
            />
          </Column>
        </Row>
        <Row id="assistance-row" span={16} columns={16}>
          <h3>
            <FormattedMessage id="bookingPath.contact.assistanceExtrasSelectionTitle" />
          </h3>
          <RadioFieldSet
            noDefault
            name="assistance-selection"
            radioButtons={options}
            selectedValue={selectedAssistanceOption}
            onChange={handleIsAssistanceSelected}
          />
        </Row>
        {isAssistanceSelected && (
          <Row id="assistance-warning" span={16} columns={16} style={{ whiteSpace: 'pre-wrap' }}>
            <AlertBox variant={AlertBox.variants.WARNING}>
              <FormattedMessage id="addAssistance.warning" />
            </AlertBox>
          </Row>
        )}
        {!isDisabled ? (
          <Row span={16} columns={16}>
            <Column span={3}>
              <Button
                data-cy="submit"
                disabled={disabledContinue || loading || shouldBlockAddingAssistance}
                loading={loading}
                type="submit"
              >
                <FormattedMessage id="groupDetails.continue" />
              </Button>
            </Column>
          </Row>
        ) : null}
      </StyledForm>
    </FormProvider>
  );
};

GroupDetails.propTypes = {
  bookingData: bookingShape,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  }),
  history: historyShape,
  updatePassenger: PropTypes.func,
  updateMinors: PropTypes.func,
  membershipNumber: PropTypes.string,
  client: clientShape,
  loading: PropTypes.bool,
  selectedSeats: PropTypes.arrayOf(
    PropTypes.shape({
      coach: PropTypes.string,
      number: PropTypes.string
    })
  ),
  selectedPassenger: passengerShape,
  selectedPassengers: PropTypes.arrayOf(passengerShape),
  updatePassengerSeats: PropTypes.func,
  onSelectNewPassenger: PropTypes.func,
  onRemovePassengerSelection: PropTypes.func,
  isDisabled: PropTypes.bool,
  disabledContinue: PropTypes.bool,
  bookingLanguage: PropTypes.string,
  setBookingLanguage: PropTypes.func,
  isAssistanceSelected: PropTypes.bool,
  setIsAssistanceSelected: PropTypes.func,
  canAddSpecialAssistanceToBooking: PropTypes.bool
};

export default GroupDetails;
